import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { CodeField } from 'components/Field/CodeField';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  REMOTE_ACCOUNT_EMAIL_CHANGE_CONFIRM,
  REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectUser } from 'store/auth/selectors';
import * as Clipboard from 'expo-clipboard';
import { useAlert } from 'hooks/useAlert';
import { selectIncomingValue } from 'store/app/selectors';
import { useEsc } from 'hooks/useEsc';
import { animateNext } from 'lib/animateNext';

export const ProfileEmailScreen: React.FC<
  ModalScreenProps<ModalScreens.SETTINGSPROFILEEMAIL>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const user = useSelector(selectUser);
  const [email, setEmail] = React.useState<string>('');
  const [token, setToken] = React.useState<string>('');
  const { features } = route.params;

  const [pastedVerificationCode, setPastedVerificationCode] =
    React.useState('');

  const incomingValueKey = 'emailChangeToken';

  const incomingValue = useSelector((state) =>
    selectIncomingValue(state, incomingValueKey),
  ) as { email: string; token: string } | undefined;

  const handlePressNext = () =>
    dispatch(REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST({ email }));

  const handlePasteVerificationCode = async () => {
    const value = await Clipboard.getStringAsync();
    const cleanValue = value
      .split('')
      .filter((v) => Number.isInteger(parseInt(v, 10)))
      .join('')
      .slice(0, 6);
    if (cleanValue) {
      setPastedVerificationCode(cleanValue);
    } else {
      alert({ text: 'Sorry, there was no code to paste' });
    }
  };

  const handleChangeCode = (code: string) => {
    if (code.length === 6) {
      dispatch(
        REMOTE_ACCOUNT_EMAIL_CHANGE_CONFIRM({
          code,
          token,
        }),
      );
    }
  };

  const handleBack = () => {
    setPastedVerificationCode('');
    setToken('');
  };

  const handleClose = () => {
    if (!token) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  React.useEffect(() => {
    if (incomingValue) {
      animateNext();
      setEmail(incomingValue.email);
      setToken(incomingValue.token);
      dispatch(SET_INCOMING_VALUE({ key: incomingValueKey }));
    }
  }, [dispatch, incomingValue]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !token });
  }, [navigation, token]);
  useEsc(handleClose);

  if (!user) {
    return null;
  }

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={features.edit ? 'Change Email' : 'Email'}
          saveButtonText={!token ? 'Next' : 'Confirm'}
          cancelButtonText={!token ? 'Close' : 'Back'}
          onPressCancelButton={!token ? handleClose : handleBack}
          onPressSaveButton={
            !features.edit
              ? undefined
              : token
                ? null
                : email
                  ? handlePressNext
                  : null
          }
        />
      }
    >
      {features.edit ? (
        <>
          <Card label="Current Email">
            <CardRow value={user.email} />
          </Card>

          <Card
            label="New Email"
            explanation={
              !token && 'A verification code will be sent to this address'
            }
          >
            {!token ? (
              <TextField
                placeholder="Enter your new email"
                onChange={setEmail}
                onSubmitEditing={handlePressNext}
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="next"
                enablesReturnKeyAutomatically
                onChangeDebounceMs={0}
                value={email}
                onEsc={handleClose}
                autoFocus
                autoFocusDelay
              />
            ) : (
              <CardRow value={email} />
            )}
          </Card>

          {!!token && (
            <Card
              label="Verification Code"
              explanation={`Enter the verification code we just emailed to ${email}`}
              bodyStyle={{ borderColor: 'white', borderWidth: 1 }}
              labelLinkText="Paste"
              onLabelLinkPress={handlePasteVerificationCode}
            >
              <CodeField
                onChange={handleChangeCode}
                rootStyle={{ backgroundColor: 'white' }}
                externalValue={pastedVerificationCode}
              />
            </Card>
          )}
        </>
      ) : (
        <Card explanation="You cannot change your email; contact your account's admin to change it">
          <CardRow label="Email" value={user.email} />
        </Card>
      )}
    </PageScrollView>
  );
};
